import { connect } from 'react-redux';
import serviceContainer from 'src/ServiceContainer';

import { SankeyGroupedView } from './SankeyGroupedView';
import { AppState, AppThunkDispatch } from 'src/store';
import { ListDataApi } from 'configuration-schemas/confdefnView';
import { SankeyGroupedViewOwnProps } from 'configuration-schemas/ownProps';
import { SankeyGroupedViewDefn, zSankeyGroupedViewDefn } from 'configuration-schemas/viewdefns/viewdefn';
import { ASSORTMENT } from 'src/utils/Domain/Constants';
import { lookBackPeriodProp } from 'src/utils/Domain/Constants';
import { ListDataOptions } from 'src/worker/pivotWorker.types';
import { ConfDefnComponentType } from 'configuration-schemas/confdefnComponents';
import { ComponentErrorType } from 'src/components/ErrorBoundary/ErrorBoundary.slice';
import { toastAndLog } from 'src/services/loggingService';
import { getTopMembers } from 'src/pages/Hindsighting/MacroTrends/Summary/Summary.container';

import {
  cleanUp,
  receiveConfig,
  receiveDetailData,
  receiveError,
  receiveSankeyData,
  requestConfig,
  requestDetailData,
  requestSankeyData,
} from './SankeyGroupedView.slice';

type SankeyAggDefn = SankeyGroupedViewDefn & { aggBy?: string };

function resolveSankeyAggBy(sankeyDefn: SankeyAggDefn | undefined, dataApi: ListDataApi): string {
  if (sankeyDefn?.aggBy && typeof sankeyDefn.aggBy === 'string') return sankeyDefn.aggBy;
  const fallback = dataApi.params?.aggBy;
  return typeof fallback === 'string' ? fallback : '';
}

function buildBaseOptions(state: AppState, aggBy: string, topMembers: string): ListDataOptions {
  return {
    aggBy,
    topMembers,
    [lookBackPeriodProp]: state.subheader.lookBackPeriod,
    nestData: false,
  };
}

const asyncFetchSankeyData = (dataApi: ListDataApi) => {
  return async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    const state = getState();
    const { pivotService } = serviceContainer;

    const sankeySlice = state.pages.allocation.sankeyGroupedView;
    const aggBy = resolveSankeyAggBy(sankeySlice.sankeyDefn, dataApi);

    const baseTopMembers = getTopMembers(state.scope.scope);
    const options = buildBaseOptions(state, aggBy, baseTopMembers);

    dispatch(requestSankeyData());

    try {
      const resp = await pivotService.listData(dataApi.defnId, dataApi.params?.appName, options);
      dispatch(receiveSankeyData(resp.tree));
    } catch (error) {
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.sankeyGroupedView,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: dataApi.defnId,
        }),
      );
    }
  };
};

const asyncFetchDetailData = (dataApi: ListDataApi, memberId?: string) => {
  return async (dispatch: AppThunkDispatch, getState: () => AppState) => {
    if (!memberId) return;
    const state = getState();
    const { pivotService } = serviceContainer;
    const detailAggBy = (typeof dataApi.params?.aggBy === 'string' && dataApi.params?.aggBy) ? dataApi.params.aggBy : 'level:month';
    const topMembers = String(memberId);

    const options = buildBaseOptions(state, detailAggBy, topMembers);

    dispatch(requestDetailData(memberId));

    try {
      const resp = await pivotService.listData(dataApi.defnId, dataApi.params?.appName, options, true);
      dispatch(receiveDetailData(resp.tree));
    } catch (error) {
      toastAndLog('An error occurred fetching detail data', error);
      dispatch(
        receiveError({
          type: ComponentErrorType.data,
          message: (error as Error)?.message,
          name: ConfDefnComponentType.sankeyGroupedView,
          stack: (error as Error)?.stack,
          issues: error,
          defnId: dataApi.defnId,
        }),
      );
    }
  };
};

const mapStateToProps = (state: AppState, ownProps: SankeyGroupedViewOwnProps) => {
  const slice = state.pages.allocation.sankeyGroupedView;

  return {
    sankeyDefn: slice.sankeyDefn,
    sankeyData: slice.sankeyData,
    sankeyDataState: slice.sankeyDataState,
    detailData: slice.detailData,
    detailDataState: slice.detailDataState,
    selectedMemberId: slice.selectedMemberId,
    title: slice.title ?? ownProps.title,
  };
};

const mapDispatchToProps = (dispatch: AppThunkDispatch, ownProps: SankeyGroupedViewOwnProps) => {
  const client = serviceContainer.tenantConfigClient;

  return {
    onShowView: () => {
      dispatch(requestConfig());
      const sankeyViewId =  ownProps.defns.view;
      if (sankeyViewId) {
        client
          .getTenantViewDefns({
            defnIds: sankeyViewId,
            appName: ASSORTMENT,
            validationSchemas: [zSankeyGroupedViewDefn],
          })
          .then((resp) => {
            const defn = (resp as SankeyGroupedViewDefn[])[0];
            dispatch(
              receiveConfig({
                sankeyDefn: {
                  ...defn,
                  metricIndex: defn.metricIndex || ownProps.metricIndex,
                },
                title: ownProps.title,
              }),
            );
          })
          .catch((error) =>
            dispatch(
              receiveError({
                type: ComponentErrorType.config,
                message: (error as Error)?.message,
                name: ConfDefnComponentType.sankeyGroupedView,
                stack: (error as Error)?.stack,
                issues: error,
                defnId: error.defnId,
              }),
            ),
          );
      } else {
        dispatch(
          receiveConfig({
            sankeyDefn: {
              id: 'SankeyGroupedViewDefault',
              metricIndex: ownProps.metricIndex,
            } as SankeyGroupedViewDefn,
            title: ownProps.title,
          }),
        );
        dispatch(asyncFetchSankeyData(ownProps.sankeyDataApi));
      }
    },
    onRefresh: () => dispatch(asyncFetchSankeyData(ownProps.sankeyDataApi)),
    onElementClick: (memberId?: string) => dispatch(asyncFetchDetailData(ownProps.detailDataApi, memberId)),
    onDestroy: () => {
      dispatch(cleanUp());
    },
  };
};

export default connect(mapStateToProps, mapDispatchToProps)(SankeyGroupedView);
